// Paywall Configuration with Multiple Options
// Based on @docs/paywall/paywall-config.js

import type { BillingProvider, CentrobillCheckoutMode } from '../domain/payment/types';

export interface PriceDefinition {
  id: string;
  one_time_price_id?: string;
  recurring_price_id: string;
  discount_id?: string;
}

export interface PaywallOption {
  title: string;
  subtitle?: string; // Optional subtitle shown below the title
  price: string; // Reference to PriceDefinition.id
  crossed_price: string; // Formula for crossed out price
  uncrossed_price: string; // Formula for display price
  daily_price: string; // Formula for per-day price
  crossed_daily_price: string; // Formula for per-day price
  pay_now: string; // Formula for immediate payment
  pay_later: string; // Formula for recurring payment
  pay_later_in_days: number;
  pay_later_recurring_period: string;
  badge?: string;
  price_postfix?: string; // Text to show after price (e.g., "weekly", "monthly")
}

export interface VoucherConfig {
  code: string;
  discount: number;
}

export interface PaywallView {
  id: string;
  voucher?: VoucherConfig;
  options: PaywallOption[];
  below: {
    items: Array<
      | { type: 'button'; text: string; action: string; style?: 'primary' | 'secondary' | 'link' }
      | { type: 'checkout' }
      | { type: 'express-checkout' }
    >;
    alternative_items?: Array<
      | { type: 'button'; text: string; action: string; style?: 'primary' | 'secondary' | 'link' }
      | { type: 'checkout' }
    >;
  };
  'on-checkout-popup-close-with-fail'?: {
    actions: Array<'show_discount_congratulations' | 'switch_view'>;
    target_view: string;    // The view ID to switch to (for switch_view action)
  };
}

export interface PaywallConfig {
  id: string;
  billing: BillingProvider;
  checkoutMode?: CentrobillCheckoutMode;  // 'token' (default) or 'page' - only for centrobill
  default_view: string;
  prices: PriceDefinition[];
  views: PaywallView[];
}

// Helper function to generate voucher codes
function generateVoucherCode(suffix: string): string {
  const now = new Date();
  const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
                      'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const month = monthNames[now.getMonth()];
  const year = now.getFullYear();
  return `${month}${year}${suffix}`;
}

// Main paywall configurations.
// Only v5 is live. Retired paywalls v1–v4 are archived in `paywall-config.ts.old`.
export const PAYWALL_CONFIGS: PaywallConfig[] = [
  {
    // Stripe equivalent of v4 (4-week / 12-week + 1-week paid trial).
    // Trial plan uses one_time_price_id ("1w") + recurring_price_id ("4w") -> 2 prices
    // -> Stripe subscription schedule (phase 1 = trial week, phase 2 = 4-week recurring).
    // The discount is applied only to the first transaction (coupon, duration: once;
    // and on schedule phase 1 only) - ongoing charges are full price.
    id: "v5",
    billing: "stripe",
    default_view: "initial_1",
    prices: [
      // 50% discount tier
      {
        id: "discount50_4w1w",
        discount_id: "discount50",
        one_time_price_id: "1w",
        recurring_price_id: "4w",
      },
      {
        id: "discount50_4w",
        discount_id: "discount50",
        recurring_price_id: "4w",
      },
      {
        id: "discount50_12w",
        discount_id: "discount50",
        recurring_price_id: "12w",
      },
      // 63% discount tier
      {
        id: "discount63_4w1w",
        discount_id: "discount63",
        one_time_price_id: "1w",
        recurring_price_id: "4w",
      },
      {
        id: "discount63_4w",
        discount_id: "discount63",
        recurring_price_id: "4w",
      },
      {
        id: "discount63_12w",
        discount_id: "discount63",
        recurring_price_id: "12w",
      },
    ],
    views: [
      {
        id: "initial_1",
        voucher: {
          code: generateVoucherCode("GLOW"),
          discount: 50,
        },
        options: [
          {
            title: "1-Week Trial",
            subtitle: "4-Week Plan",
            price: "discount50_4w1w",
            crossed_price: "${one_time_price_id_original}",
            uncrossed_price: "${one_time_price_id_discounted}",
            crossed_daily_price: "${one_time_price_id_original}/7",
            daily_price: "${one_time_price_id_discounted}/7",
            pay_now: "${one_time_price_id_discounted}",
            pay_later: "${recurring_price_id_original}",
            pay_later_in_days: 7,
            pay_later_recurring_period: "every 4 weeks",
          },
          {
            title: "4-Week Plan",
            price: "discount50_4w",
            crossed_price: "${recurring_price_id_original}",
            uncrossed_price: "${recurring_price_id_discounted}",
            crossed_daily_price: "${recurring_price_id_original}/28",
            daily_price: "${recurring_price_id}/28",
            pay_now: "${recurring_price_id}",
            pay_later: "${recurring_price_id_original}",
            pay_later_in_days: 28,
            pay_later_recurring_period: "every 4 weeks",
            badge: "MOST POPULAR",
          },
          {
            title: "12-Week Plan",
            price: "discount50_12w",
            crossed_price: "${recurring_price_id_original}",
            uncrossed_price: "${recurring_price_id_discounted}",
            crossed_daily_price: "${recurring_price_id_original}/84",
            daily_price: "${recurring_price_id}/84",
            pay_now: "${recurring_price_id}",
            pay_later: "${recurring_price_id_original}",
            pay_later_in_days: 84,
            pay_later_recurring_period: "every 12 weeks",
          },
        ],
        below: {
          items: [
            {
              type: "express-checkout",
            },
            {
              type: "button",
              text: "More payment methods",
              action: "open-checkout-popup",
              style: "link",
            }
          ],
          alternative_items: [
            {
              type: "button",
              text: "Get my plan",
              action: "open-checkout-popup",
            }
          ],
        },
        'on-checkout-popup-close-with-fail': {
          actions: ["show_discount_congratulations", "switch_view"],
          target_view: "extra_discount_1",
        }
      },
      {
        id: "extra_discount_1",
        voucher: {
          code: generateVoucherCode("PLUS"),
          discount: 63,
        },
        options: [
          {
            title: "1-Week Trial",
            subtitle: "4-Week Plan",
            price: "discount63_4w1w",
            crossed_price: "${one_time_price_id_original}",
            uncrossed_price: "${one_time_price_id_discounted}",
            crossed_daily_price: "${one_time_price_id_original}/7",
            daily_price: "${one_time_price_id_discounted}/7",
            pay_now: "${one_time_price_id_discounted}",
            pay_later: "${recurring_price_id_original}",
            pay_later_in_days: 7,
            pay_later_recurring_period: "every 4 weeks",
          },
          {
            title: "4-Week Plan",
            price: "discount63_4w",
            crossed_price: "${recurring_price_id_original}",
            uncrossed_price: "${recurring_price_id_discounted}",
            crossed_daily_price: "${recurring_price_id_original}/28",
            daily_price: "${recurring_price_id}/28",
            pay_now: "${recurring_price_id}",
            pay_later: "${recurring_price_id_original}",
            pay_later_in_days: 28,
            pay_later_recurring_period: "every 4 weeks",
            badge: "MOST POPULAR",
          },
          {
            title: "12-Week Plan",
            price: "discount63_12w",
            crossed_price: "${recurring_price_id_original}",
            uncrossed_price: "${recurring_price_id_discounted}",
            crossed_daily_price: "${recurring_price_id_original}/84",
            daily_price: "${recurring_price_id}/84",
            pay_now: "${recurring_price_id}",
            pay_later: "${recurring_price_id_original}",
            pay_later_in_days: 84,
            pay_later_recurring_period: "every 12 weeks",
          },
        ],
        below: {
          items: [
            {
              type: "express-checkout",
            },
            {
              type: "button",
              text: "More payment methods",
              action: "open-checkout-popup",
              style: "link",
            }
          ],
          alternative_items: [
            {
              type: "button",
              text: "Get my plan",
              action: "open-checkout-popup",
            }
          ],
        }
      },
    ]
  },
];

export const DEFAULT_PAYWALL_ID = 'v5';

export const PAYWALL_TIMER_DURATION = 600; // seconds

export const PAYWALL_FEATURES: string[] = [
  'Unlimited access to personalized skincare routines',
  'AI-powered skin analysis & tracking',
  'Progress photos & timeline',
  'Expert skincare guides & tutorials',
  'Community access & support',
  'Daily reminders & habit tracking'
];

export const PAYWALL_TRUST_ELEMENTS: Array<{ icon: string; title: string; subtitle: string }> = [
  {
    icon: '🛡️',
    title: '14-Day Money-Back Guarantee',
    subtitle: 'Try risk-free for 14 days'
  },
  {
    icon: '🔒',
    title: 'Secure Payment by Paddle',
    subtitle: 'Bank-level encryption'
  },
  {
    icon: '❌',
    title: 'Cancel Anytime',
    subtitle: 'No contracts, no commitments'
  }
];

const SUCCESS_MESSAGES: Record<string, string> = {
  'clear-acne': 'Get ready for clear, radiant skin!',
  'reduce-aging': 'Your journey to youthful skin starts now!',
  'even-tone': 'Say hello to even, glowing skin!',
  'hydrate': 'Deep hydration is on its way!',
  'calm-redness': 'Calm, comfortable skin awaits!',
  'skip': 'Get ready for clear, radiant skin!',
};

export function getSuccessMessage(primaryGoal: string): string {
  return SUCCESS_MESSAGES[primaryGoal] || 'Your personalized skincare journey starts now!';
}

export function resolvePaywallConfig(id?: string): PaywallConfig | undefined {
  if (id) {
    return PAYWALL_CONFIGS.find(config => config.id === id);
  }
  return getDefaultPaywallConfig();
}

export function getDefaultPaywallConfig(): PaywallConfig | undefined {
  return PAYWALL_CONFIGS.find(config => config.id === DEFAULT_PAYWALL_ID) || PAYWALL_CONFIGS[0];
}

export function listPaywallConfigs(): PaywallConfig[] {
  return PAYWALL_CONFIGS;
}

/**
 * Returns the plan labels (option titles) for a paywall view, deduped and in
 * config order. Used by UI that must reflect the currently active plans (e.g.
 * the social-proof carousel), so it never advertises plans the paywall doesn't
 * actually offer. Resolves the config by id (or default) and the view by id (or
 * the config's `default_view`). Returns [] if nothing resolves.
 */
export function getPaywallPlanLabels(paywallId?: string, viewId?: string): string[] {
  const config = resolvePaywallConfig(paywallId);
  if (!config) return [];
  const view = (viewId ? config.views.find(v => v.id === viewId) : undefined)
    || config.views.find(v => v.id === config.default_view)
    || config.views[0];
  if (!view) return [];
  const titles = view.options.map(option => option.title).filter(Boolean);
  return [...new Set(titles)];
}
